import { config } from "src/utils/config";

const INVALID_STATE_VALUES = new Set(["", "null", "undefined"]);

const ETSY_CLIENT_ID = "c3f3ejj8l0jra9u06rukbxxx";
const ETSY_SCOPES =
  "listings_r listings_w shops_r shops_w profile_r profile_w transactions_r transactions_w";
const AMAZON_APPLICATION_ID =
  "amzn1.sp.solution.6ac59542-55dc-4486-9d5b-edee1dc63dcf";
const CANVA_CLIENT_ID = "OC-AZxX1kn7wclI";
const CANVA_SCOPES =
  "design:meta:read design:permission:read design:content:read asset:read";
const CODE_CHALLENGE = "3ERlZgHM0JsbTM95OAxzZvD4ggq5xYgnf7yC2F-jJS8";

export const isUsableOAuthState = (value) => {
  if (value == null) return false;
  return !INVALID_STATE_VALUES.has(String(value).trim());
};

const readPersistedAuthToken = () => {
  if (typeof localStorage === "undefined") return "";
  try {
    const raw = localStorage.getItem("persist:root");
    if (!raw) return "";
    const root = JSON.parse(raw);
    const userState =
      typeof root?.user === "string" ? JSON.parse(root.user) : root?.user;
    const token = userState?.token;
    return isUsableOAuthState(token) ? String(token).trim() : "";
  } catch {
    return "";
  }
};

export const getAuthToken = (reduxToken) => {
  if (isUsableOAuthState(reduxToken)) return String(reduxToken).trim();
  if (typeof localStorage === "undefined") return "";
  const stored = localStorage.getItem("token");
  if (isUsableOAuthState(stored)) return stored.trim();
  return readPersistedAuthToken();
};

export const getAuthorizeUrlState = (url) => {
  if (!url) return "";
  try {
    return new URL(url).searchParams.get("state") || "";
  } catch {
    return "";
  }
};

export const isSafeStoreAuthorizeUrl = (url) =>
  isUsableOAuthState(getAuthorizeUrlState(url));

export const getCanvaOAuthState = (reduxToken) => {
  if (typeof localStorage !== "undefined") {
    const storedCanvaState = localStorage.getItem("canva_state");
    if (isUsableOAuthState(storedCanvaState)) {
      return storedCanvaState.trim();
    }
  }
  return getAuthToken(reduxToken);
};

export const persistCanvaOAuthState = (payload = {}) => {
  const user =
    payload.user && typeof payload.user === "object" ? payload.user : {};
  const canvaState =
    payload.canva_state ||
    user.canva_state ||
    payload.canvaState ||
    user.canvaState ||
    "";

  if (isUsableOAuthState(canvaState)) {
    localStorage.setItem("canva_state", String(canvaState).trim());
  }
};

export const getBlockedStoreConnectMessage = (storeName) =>
  `Can't connect ${storeName}. Navigation was blocked because your login token is missing, so the OAuth state would be empty or null. Please log in again, then try connecting.`;

const toQuery = (params) =>
  Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");

export const getEtsyAuthorizeUrl = (reduxToken) => {
  const state = getAuthToken(reduxToken);
  if (!state) return "";

  return `https://www.etsy.com/oauth/connect?${toQuery({
    response_type: "code",
    redirect_uri: `${config.apiUrl}/oauth`,
    scope: ETSY_SCOPES,
    client_id: ETSY_CLIENT_ID,
    state,
    code_challenge: CODE_CHALLENGE,
    code_challenge_method: "S256",
  })}`;
};

export const getAmazonAuthorizeUrl = (reduxToken) => {
  const state = getAuthToken(reduxToken);
  if (!state) return "";

  return `https://sellercentral.amazon.com/apps/authorize/consent?${toQuery({
    application_id: AMAZON_APPLICATION_ID,
    redirect_uri: `${config.apiUrl}/oauth/amazon`,
    state,
  })}`;
};

export const getCanvaAuthorizeUrl = (reduxToken) => {
  const state = getCanvaOAuthState(reduxToken);
  if (!state) return "";

  return `https://www.canva.com/api/oauth/authorize?${toQuery({
    client_id: CANVA_CLIENT_ID,
    response_type: "code",
    code_challenge_method: "s256",
    scope: CANVA_SCOPES,
    redirect_uri: `${config.apiUrl}/oauth/canva`,
    code_challenge: CODE_CHALLENGE,
    state,
  })}`;
};
